import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { setPostAuthIntent } from '../utils/postAuth';
import Price from '../components/Price';
import ContactOwnerModal from '../components/ContactOwnerModal';
import './Listing.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function Spec({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="spec-row">
      <span className="spec-label">{label}</span>
      <span className="spec-value">{value}</span>
    </div>
  );
}

export default function Listing() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addItem, has } = useCart();
  const { t } = useTranslation();
  const [listing, setListing] = useState(null);
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [contact, setContact] = useState(null); // { publicationId, name, ownerName }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/api/listings/${id}`)
      .then(({ data }) => { if (!cancelled) setListing(data.listing); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.message || t('listing.load_error')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, t]);

  if (loading) return <div className="listing-state">{t('listing.loading')}</div>;
  if (error)   return <div className="listing-state listing-state--err">{error}</div>;
  if (!listing) return <div className="listing-state">{t('listing.not_found')}</div>;

  const {
    images = [], name, brand, category, material, scale,
    sku, toyNumber, launchYear, description, views, locked, ownersCount,
    firstPublisher, priceStats, marketValue, sellers = [], traders = [],
  } = listing;

  const hasMarketValue = Number(marketValue?.amount) > 0;

  // Sin sesión: recuerda esta pieza para volver directo a ella una vez que el
  // usuario complete el registro o el login (journey del botón "Ingresar").
  const goAuth = () => {
    setPostAuthIntent({ type: 'listing', id });
    navigate('/trigger');
  };

  const hasGallery = images.length > 0;
  const publisher = firstPublisher;
  const publisherName = publisher?.username || publisher?.name || t('listing.collector');
  const publisherAvatar = publisher?.avatar
    || `https://ui-avatars.com/api/?name=${encodeURIComponent(publisherName)}&background=d4a58a&color=fff&size=64`;

  return (
    <>
      <Helmet>
        <title>{name} — Troly</title>
        <meta name="description" content={`${name} · ${brand}${scale ? ` · ${scale}` : ''} · Troly Marketplace`} />
      </Helmet>

      <main className="listing-page">
        <div className="container listing-grid">
          {/* ── Left: gallery ── */}
          <div className="listing-gallery">
            <div className="gallery-main">
              {hasGallery ? (
                <img src={images[activeImg]} alt={name} />
              ) : (
                <div className="gallery-empty">{t('listing.no_image')}</div>
              )}
            </div>
            {images.length > 1 && (
              <div className="gallery-thumbs">
                {images.map((src, i) => (
                  <button
                    key={src}
                    className={`gallery-thumb${i === activeImg ? ' gallery-thumb--active' : ''}`}
                    onClick={() => setActiveImg(i)}
                    aria-label={`${t('listing.image_alt')} ${i + 1}`}
                  >
                    <img src={src} alt="" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── Center: title + specs ── */}
          <div className="listing-info">
            <p className="listing-eyebrow">{category}</p>
            <h1 className="listing-title">{name}</h1>

            <div className="spec-block">
              <Spec label={t('listing.brand')}        value={brand} />
              <Spec label={t('listing.category')}     value={category} />
              <Spec label={t('listing.material')}     value={material} />
              <Spec label={t('listing.launch_year')}  value={launchYear} />
              <Spec label={t('listing.sku')}          value={sku} />
              <Spec label={t('listing.toy_number', { defaultValue: 'Toy Number' })} value={toyNumber} />
              <Spec label={t('listing.scale')}        value={scale} />
              <Spec label={t('listing.owners', { defaultValue: 'Coleccionistas' })} value={ownersCount} />
              <Spec label={t('listing.views')}        value={views} />
            </div>

            {locked ? (
              <div className="paywall-box paywall-box--inline">
                <p className="paywall-eyebrow">{t('listing.description')}</p>
                <p className="paywall-text">{t('listing.paywall_desc')}</p>
              </div>
            ) : description ? (
              <div className="listing-desc">
                <p className="spec-label" style={{ marginBottom: 8 }}>{t('listing.description')}</p>
                <p>{description}</p>
              </div>
            ) : null}
          </div>

          {/* ── Right: side panel (publisher + price ranges + sellers) ── */}
          <aside className="listing-side">
            <div className="side-card">
              <p className="side-eyebrow">{t('listing.published_by')}</p>
              <Link to={publisher?._id ? `/u/${publisher._id}` : '#'} className="side-owner">
                <img src={publisherAvatar} alt={publisherName} className="side-owner__av" />
                <div>
                  <p className="side-owner__name">{publisherName}</p>
                  {publisher?.location && <p className="side-owner__loc">{publisher.location}</p>}
                </div>
              </Link>
            </div>

            <div className="side-card">
              <p className="side-eyebrow">{t('listing.price')}</p>
              {locked ? (
                <div className="paywall-box">
                  <p className="paywall-text">
                    {user
                      ? t('listing.paywall_logged')
                      : t('listing.paywall_anon')}
                  </p>
                  <button
                    className="btn-teal"
                    style={{ width: '100%', marginTop: 12 }}
                    onClick={() => (user ? navigate('/profile') : goAuth())}
                  >
                    {user ? t('listing.subscribe') : t('listing.sign_in')}
                  </button>
                </div>
              ) : priceStats || hasMarketValue ? (
                <div className="price-stats">
                  {priceStats && (
                    <>
                      <div className="price-stats__row">
                        <span className="price-stats__label">{t('listing.price_low')}</span>
                        <Price className="price-stats__val" amount={priceStats.low} currency={priceStats.currency || 'USD'} />
                      </div>
                      <div className="price-stats__row">
                        <span className="price-stats__label">{t('listing.price_mid')}</span>
                        <Price className="price-stats__val" amount={priceStats.mid} currency={priceStats.currency || 'USD'} />
                      </div>
                      <div className="price-stats__row">
                        <span className="price-stats__label">{t('listing.price_high')}</span>
                        <Price className="price-stats__val" amount={priceStats.high} currency={priceStats.currency || 'USD'} />
                      </div>
                    </>
                  )}
                  {hasMarketValue && (
                    <>
                      <div
                        className="price-stats__row"
                        style={priceStats ? { borderTop: '1px solid var(--line)', paddingTop: 8, marginTop: 8 } : undefined}
                      >
                        <span className="price-stats__label">{t('listing.market_value', { defaultValue: 'Valor de referencia' })}</span>
                        <Price className="price-stats__val" amount={marketValue.amount} currency={marketValue.currency || 'USD'} />
                      </div>
                      <p style={{ fontSize: 11, color: 'var(--muted)', margin: '6px 0 0' }}>
                        {t('listing.market_value_note', { defaultValue: 'Estimado de mercado — no es una oferta de venta.' })}
                      </p>
                    </>
                  )}
                </div>
              ) : (
                <p className="side-price">{t('listing.consult')}</p>
              )}
            </div>

            {traders.length > 0 && (
              <div className="side-card">
                <p className="side-eyebrow">{t('listing.traders_title')}</p>
                <div style={{ display: 'grid', gap: 10 }}>
                  {traders.map((tr) => {
                    const trName = tr.username || tr.name;
                    const isOwn = user && user._id === tr._id;
                    return (
                      <div key={tr._id} style={{ padding: 10, border: '1px solid var(--line)', borderRadius: 8, background: 'var(--bg)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Link to={`/u/${tr._id}`} style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink)', textDecoration: 'none' }}>
                            {trName}
                          </Link>
                          <span style={{
                            fontSize: 10, fontWeight: 700, letterSpacing: '.06em',
                            padding: '2px 8px', borderRadius: 12,
                            background: 'rgba(245,158,11,.14)', color: '#f59e0b',
                          }}>
                            CAMBIO
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                          {isOwn ? (
                            <span className="btn-ghost" style={{ flex: 1, padding: '8px 10px', fontSize: 12, textAlign: 'center' }}>—</span>
                          ) : (
                            <button
                              type="button"
                              className="btn-teal"
                              style={{ flex: 1, padding: '8px 10px', fontSize: 12 }}
                              onClick={() => {
                                if (!user) { goAuth(); return; }
                                setContact({ publicationId: tr.publicationId, name, ownerName: trName });
                              }}
                            >
                              ✉️ {t('listing.contact_owner')}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {sellers.length > 0 && (
              <div className="side-card">
                <p className="side-eyebrow">{t('listing.sellers_title', { defaultValue: 'Quién la vende' })}</p>
                <div style={{ display: 'grid', gap: 10 }}>
                  {sellers.map((s) => {
                    const sName = s.username || s.name;
                    const isSeller = s.role === 'seller';
                    const inCart = has(s.publicationId);
                    const sAmount = Number(s.price?.amount) || 0;
                    const sCurrency = s.price?.currency || 'USD';
                    // Cualquier usuario puede vender. Mostrar carrito si hay
                    // inventario, precio y no es el propio dueño.
                    const canBuy = s.available > 0 && sAmount > 0 && (!user || user._id !== s._id);
                    return (
                      <div key={s._id} style={{ padding: 10, border: '1px solid var(--line)', borderRadius: 8, background: 'var(--bg)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Link to={`/u/${s._id}`} style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink)', textDecoration: 'none' }}>
                            {sName}
                          </Link>
                          <span style={{
                            fontSize: 10, fontWeight: 700, letterSpacing: '.06em',
                            padding: '2px 8px', borderRadius: 12,
                            background: isSeller ? 'rgba(20,184,166,.14)' : 'rgba(148,163,184,.14)',
                            color: isSeller ? 'var(--teal)' : 'var(--muted)',
                          }}>
                            {isSeller ? 'VENDEDOR' : 'USER'}
                          </span>
                        </div>
                        {sAmount > 0 && (
                          <div style={{ fontSize: 13, marginTop: 4 }}>
                            <Price amount={sAmount} currency={sCurrency} />
                          </div>
                        )}
                        {isSeller && (
                          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                            {s.available > 0 ? `${s.available} de ${s.units} disponibles` : 'Sin stock'}
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                          {canBuy ? (
                            <button
                              type="button"
                              className="btn-teal"
                              style={{ flex: 1, padding: '8px 10px', fontSize: 12 }}
                              onClick={() => {
                                if (inCart) { navigate('/cart'); return; }
                                if (!user) { goAuth(); return; }
                                addItem({
                                  publicationId: s.publicationId,
                                  listingId: listing._id,
                                  name,
                                  image: images?.[0] || '',
                                  price: sAmount,
                                  currency: sCurrency,
                                  sellerId: s._id,
                                  sellerName: sName,
                                  units: s.available,
                                });
                              }}
                            >
                              🛒 {inCart ? t('listing_card.view_cart') : t('listing_card.add_to_cart')}
                            </button>
                          ) : (
                            <Link to={`/u/${s._id}`} className="btn-ghost" style={{ flex: 1, padding: '8px 10px', fontSize: 12, textAlign: 'center', textDecoration: 'none' }}>
                              {t('listing.contact_seller')}
                            </Link>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </aside>
        </div>
      </main>

      <ContactOwnerModal
        publicationId={contact?.publicationId || null}
        pieceName={contact?.name || ''}
        ownerName={contact?.ownerName || ''}
        onClose={() => setContact(null)}
      />
    </>
  );
}
